import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Reveal } from "../components/site/Motion";
import { SectionLabel } from "../components/site/Bits";
import { POSTS } from "../lib/posts";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function Blog() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Blog & Guides | RenewTracker";
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;
    if (descEl)
      descEl.setAttribute(
        "content",
        "Practical guides on tracking document expiry dates, managing renewals and staying organised with RenewTracker."
      );
    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main data-testid="blog-page" className="bg-background pt-28">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="mesh-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Blog &amp; Guides</SectionLabel>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tighter text-ink sm:text-5xl">
              Practical guides on renewals and expiry tracking.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              Simple, useful articles for individuals, professionals and small businesses who want to stay ahead of renewal deadlines.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-14 lg:px-8">
        <div className="grid grid-cols-1 gap-6">
          {POSTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.06}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-brand/20 hover:shadow-xl sm:p-8"
              >
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="rounded-full bg-brand/5 px-3 py-1 font-semibold text-brand">{p.tag}</span>
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Calendar className="h-3.5 w-3.5" /> {formatDate(p.datePublished)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Clock className="h-3.5 w-3.5" /> {p.readTime}
                  </span>
                </div>
                <h2 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight text-ink sm:text-3xl">
                  {p.title}
                </h2>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{p.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                  Read article
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
